import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../contexts/ToastContext';
import { window } from '../../globals';
import useTeamGroups from '../../hooks/api/teams/teamGroups/useTeamGroups';
import { useTeamsReleaseGroups } from '../../hooks/api/teams/useTeamReleaseGroups';
import useTeams from '../../hooks/api/teams/useTeams';
import useToastedMutation from '../../ReactQuery/useToastedMutation';
import { get } from '../../redux/api';
import TeamsModule from '../../redux/teams';
import utils from '../../utils';
import Card from '../generic/Card/Card';
import DropdownButton from '../generic/DropdownButton';
import LabeledCheckbox from '../generic/Forms/LabeledCheckbox';
import { icons, IconText } from '../generic/Icon';
import Select from '../generic/Select/Select';
import { issueDashboardFeature } from '../settings/billing/features/consts';
import type { SelectOption, Task, TeamSelection } from '../../types';
import type { AddJobFn } from '../JobNotificationHub/types';

import DashboardReportCard from './DashboardReportCard';
import { GlobalIssueCSVExportMetricsPreview } from './GlobalIssueCSVExportMetricsPreview';

import css from './GlobalIssueCSVExportCard.module.less';

const GenerateReportIconText = <IconText icon={icons.addToProject} text="Generate report" />;

type Props = {
  onAddJobToNotificationQueue: AddJobFn;
};

/**
 * The "Global Issue CSV Export" card on the Reports dashboard. Scope comes from the "All projects" breadcrumb,
 * like the other report cards. When a team or team group is selected there (a team group is a `Teams` row, so
 * it arrives as its own id - the `teamId=[groupId]` rollup), the card reveals a release-group drill-down and two
 * include-flags: the flat issues list and/or the issue counts over time. Org-wide/unassigned falls back to the
 * plain issues export.
 */
export const GlobalIssueCSVExportCard: VoidFunctionComponent<Props> = ({ onAddJobToNotificationQueue }) => {
  const addToast = useAddToast();

  const teamSelection = useSelector(TeamsModule.selectors.getTeamSelection);
  // A non-empty team/group selection is the drill-down context; `[]` is all projects, `'null'` is unassigned.
  const selectedTeamIds = Array.isArray(teamSelection) ? teamSelection : [];
  const isTeamContext = selectedTeamIds.length > 0;
  // The release-group picker spans the whole breadcrumb selection, so a stable key of the selected ids
  // drives both the candidate fetch and the drill-down reset.
  const selectionKey = selectedTeamIds.join(',');

  const [releaseGroupId, setReleaseGroupId] = useState<number | null>(null);
  const [shouldIncludeIssuesList, setShouldIncludeIssuesList] = useState(true);
  const [shouldIncludeDailyCounts, setShouldIncludeDailyCounts] = useState(false);

  // Reset the release-group drill-down when the breadcrumb selection changes, so a stale selection from the
  // previous scope isn't sent to the preview/export endpoints.
  useEffect(() => {
    setReleaseGroupId(null);
  }, [selectionKey]);

  // When a release group is selected the counts export is a release-comparison series (release_counts.csv)
  // instead of the team/org daily series (issue_counts.csv); both are driven by the same include flag.
  const isReleaseGroupScoped = releaseGroupId != null;

  const { releaseGroups } = useTeamsReleaseGroups(
    selectedTeamIds,
    { page: 1, pageSize: 100, sort: 'asc', skipPagination: true },
    isTeamContext,
  );
  const releaseGroupOptions: Array<SelectOption<number>> = releaseGroups.map((releaseGroup) => ({
    label: releaseGroup.title,
    value: releaseGroup.id,
  }));
  const selectedReleaseGroupOption = releaseGroupOptions.find((option) => option.value === releaseGroupId) ?? null;

  // Resolve the breadcrumb selection to names so we can show what's scoped (a team group is a `Teams` row too).
  const { data: teams } = useTeams(isTeamContext);
  const { data: teamGroups } = useTeamGroups(isTeamContext);
  const selectedNames = [...(teams ?? []), ...(teamGroups ?? [])]
    .filter((team) => selectedTeamIds.includes(team.id))
    .map((team) => team.name);

  // Pass the breadcrumb filter through: `'null'` (unassigned) as-is, a real selection scoped, all-projects off.
  let exportTeamIds: TeamSelection | undefined;
  if (teamSelection === 'null') {
    exportTeamIds = 'null';
  } else if (selectedTeamIds.length > 0) {
    exportTeamIds = selectedTeamIds;
  }
  const exportReleaseGroupId = isTeamContext && releaseGroupId != null ? releaseGroupId : undefined;

  const hasNothingSelected = isTeamContext && !shouldIncludeIssuesList && !shouldIncludeDailyCounts;

  // The bundled report download URL (GET /api/v2/issues/csv/global). One zip carries the issues list and/or
  // the issue counts, selected via the include flags. Org-wide (no team context) omits the flags and gets the
  // default issues list. With a release group selected, the counts come back as the release-comparison series.
  const bundleDownloadUrl = () => {
    const params = new URLSearchParams();
    if (exportTeamIds === 'null') {
      params.set('teamIds', 'null');
    } else if (exportTeamIds) {
      exportTeamIds.forEach((id) => params.append('teamIds[]', String(id)));
    }
    if (exportReleaseGroupId != null) {
      params.set('releaseGroupId', String(exportReleaseGroupId));
    }
    if (isTeamContext) {
      params.set('includeIssuesList', String(shouldIncludeIssuesList));
      if (shouldIncludeDailyCounts) {
        params.set('includeDailyCounts', 'true');
      }
    }
    const query = params.toString();
    return `/api/v2/issues/csv/global${query ? `?${query}` : ''}`;
  };

  // Emailing the bundle is an async job that builds the same zip (issues list and/or daily counts) and mails a
  // link. The mutation surfaces its outcome as a toast and, on success, hands the job to the notification queue.
  const emailExport = useToastedMutation<Task | undefined>({
    mutationFn: async () => {
      const response = await get<Task>('/api/v2/issues/csv/global', false, {
        email: true,
        ...(exportTeamIds ? { teamIds: exportTeamIds } : {}),
        ...(exportReleaseGroupId != null ? { releaseGroupId: exportReleaseGroupId } : {}),
        ...(isTeamContext
          ? { includeIssuesList: shouldIncludeIssuesList, includeDailyCounts: shouldIncludeDailyCounts }
          : {}),
      });
      if (!response.ok) {
        throw new Error('Failed to queue CSV export');
      }
      return response.data;
    },
    successToastMessage: 'Your report is being generated.',
    errorToastMessage: 'Failed to generate the report.',
    onSuccess: (task) => {
      if (task?.jobToken) {
        onAddJobToNotificationQueue(task.jobToken, {
          success: { content: 'Global Issue CSV Export report emailed.' },
          failure: { content: 'Global Issue CSV Export report failed.' },
        });
      }
    },
  });

  // DropdownButton's onSelect passes the selected item's eventKey; coerce it to our delivery method.
  const generate = (eventKey?: unknown) => {
    // Premium gate: mirrors the legacy card's paywall (the export is also enforced server-side).
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal({ focusFeature: issueDashboardFeature });
      return;
    }

    const method: 'download' | 'email' = eventKey === 'email' ? 'email' : 'download';

    // Email queues the async job with the same include flags; the mutation owns the toast + job-queue handoff.
    if (method === 'email') {
      emailExport.mutate();
      return;
    }

    // A single bundled zip download (issues list and/or daily counts); acknowledge it with one toast.
    utils.openInNewTab(bundleDownloadUrl());
    addToast({ type: 'success', content: 'Your report is being generated.' });
  };

  const chipLabels = selectedNames.length > 0 ? selectedNames : ['the selected team'];
  const description = isTeamContext ? (
    <>
      Issue reports in CSV format for{' '}
      {chipLabels.map((name, index) => (
        <Fragment key={name}>
          {index > 0 && ', '}
          <span className={css.chip}>{name}</span>
        </Fragment>
      ))}
      .
    </>
  ) : (
    'Organization-wide issue reports in CSV format. Contains separate reports for license, security, and quality issues.'
  );

  return (
    <DashboardReportCard title="Global Issue CSV Export" description={description}>
      {isTeamContext && (
        <div className={css.teamPanelWrapper}>
          <Card variant="outline" color="gray" padding="none" isFullWidth>
            <Card.Content>
              <div className={css.teamPanel}>
                <div className={css.field}>
                  <span className={css.fieldLabel}>Release group</span>
                  <Select<SelectOption<number>>
                    id="teamGroupReleaseGroup"
                    isClearable
                    placeholder="All release groups"
                    value={selectedReleaseGroupOption}
                    options={releaseGroupOptions}
                    onChange={(option) => setReleaseGroupId(option?.value ?? null)}
                  />
                </div>

                <div className={css.checkboxes}>
                  <LabeledCheckbox
                    label="Include issues list"
                    tooltip="A CSV of every current finding (license, security, and quality) with its dependency, project, and status."
                    isChecked={shouldIncludeIssuesList}
                    onChange={(_event, checked) => setShouldIncludeIssuesList(checked)}
                  />
                  <LabeledCheckbox
                    label="Include issue counts over time"
                    tooltip={isReleaseGroupScoped
                      ? 'A CSV comparing issue counts across each release in the group: new, remediated, and unchanged.'
                      : 'A CSV of daily active, ignored, and remediated issue counts per category over the last 30 days.'}
                    isChecked={shouldIncludeDailyCounts}
                    onChange={(_event, checked) => setShouldIncludeDailyCounts(checked)}
                  />
                </div>

                {/* Counts are release-scoped when a release group is selected, else across the selected teams. */}
                <GlobalIssueCSVExportMetricsPreview
                  teamIds={selectedTeamIds}
                  releaseGroupId={releaseGroupId}
                  enabled={isTeamContext}
                />
              </div>
            </Card.Content>
          </Card>
        </div>
      )}

      <DropdownButton
        id="GlobalIssueCSVExportOptions"
        title={GenerateReportIconText}
        size="large"
        onSelect={generate}
        disabled={hasNothingSelected || emailExport.isPending}
      >
        <DropdownButton.Item eventKey="download">Download</DropdownButton.Item>
        <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
      </DropdownButton>
    </DashboardReportCard>
  );
};
